/**
 * The crystal, as a pure function.
 *
 * The server derives XP (see the crystal migration); this module turns XP into
 * a level and a set of visual traits, and turns a user id into a colour. Both
 * are deterministic, so the same user sees the same crystal on every device
 * and nothing about it needs to be stored.
 */

export const STAGES = [
  { level: 1,  name: 'Seed',      blurb: 'A single shard, newly woken.' },
  { level: 2,  name: 'Shard',     blurb: 'It has learned your rhythm.' },
  { level: 3,  name: 'Cluster',   blurb: 'New facets catch the light.' },
  { level: 4,  name: 'Prism',     blurb: 'Light begins to orbit it.' },
  { level: 5,  name: 'Geode',     blurb: 'A hidden interior, glowing.' },
  { level: 6,  name: 'Beacon',    blurb: 'It can be seen from afar.' },
  { level: 7,  name: 'Lumen',     blurb: 'A halo settles around it.' },
  { level: 8,  name: 'Nova',      blurb: 'It burns with quiet force.' },
  { level: 9,  name: 'Halo',      blurb: 'The ring turns on its own.' },
  { level: 10, name: 'Aurora',    blurb: 'It bends light into colour.' },
  { level: 11, name: 'Celestial', blurb: 'Almost too bright to hold.' },
  { level: 12, name: 'Eternal',   blurb: 'Fully grown. Fully yours.' },
];
export const MAX_LEVEL = STAGES.length;

/** XP needed to *reach* a level. Gently super-linear: early levels come fast. */
export function threshold(level) {
  if (level <= 1) return 0;
  return Math.round(50 * Math.pow(level - 1, 1.7));
}

/**
 * @param {number} xp
 * @returns {{level:number, name:string, blurb:string, into:number, span:number,
 *            progress:number, next:string|null, xpToNext:number|null}}
 */
export function levelFor(xp) {
  const x = Math.max(0, Number(xp) || 0);
  let level = 1;
  while (level < MAX_LEVEL && x >= threshold(level + 1)) level++;
  const stage = STAGES[level - 1];
  const from = threshold(level);
  const to = level < MAX_LEVEL ? threshold(level + 1) : from;
  const span = Math.max(1, to - from);
  const into = level < MAX_LEVEL ? x - from : span;
  return {
    level, name: stage.name, blurb: stage.blurb, into, span,
    progress: level < MAX_LEVEL ? Math.min(1, into / span) : 1,
    next: level < MAX_LEVEL ? STAGES[level].name : null,
    xpToNext: level < MAX_LEVEL ? to - x : null,
  };
}

/** Small, fast, deterministic hash → 32-bit int. */
export function hashSeed(str) {
  let h = 2166136261;
  for (let i = 0; i < String(str).length; i++) {
    h ^= String(str).charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** mulberry32: tiny seeded PRNG so a crystal's shards are stable per user. */
export function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A hue for a user. Avoids the muddy 60–140° band (olive/leaf) on purpose. */
export function crystalHue(userId) {
  const h = hashSeed(userId ?? 'seed') % 300;   // 0..299
  return h < 60 ? h : h + 60;                    // skip 60..119
}

/** Visual traits that grow with level. Everything the renderer needs. */
export function crystalTraits(level) {
  const l = Math.min(MAX_LEVEL, Math.max(1, level));
  return {
    shards:   Math.min(11, 1 + Math.floor(l * 0.9)),  // 1 → 11
    height:   0.55 + (l / MAX_LEVEL) * 0.45,          // relative size
    glow:     0.25 + (l / MAX_LEVEL) * 0.75,
    orbit:    l >= 4 ? Math.min(6, l - 2) : 0,        // orbiting motes
    halo:     l >= 7,
    ring:     l >= 9,
    prismatic: l >= 10,
    sparkles: Math.min(14, l * 1.3) | 0,
  };
}

// ---------------------------------------------------------------- growth
/**
 * Continuous growth in [0, 1] from XP. Level boundaries are only labels; the
 * crystal itself grows a little with every point, so it never jumps.
 * Inside a level it follows the level's own progress, which keeps the
 * displayed "x to next stage" and the visible growth in step.
 */
export function growthFor(xp) {
  const { level, progress } = levelFor(xp);
  return Math.min(1, ((level - 1) + progress) / (MAX_LEVEL - 1));
}

/**
 * The plan for a cluster: where each point sits on the rock, how it leans,
 * how large it will eventually be, and when in the crystal's life it starts
 * to form. Deterministic per seed, so every device draws the same cluster.
 */
export const SHARDS = 26;
export function shardPlan(seed) {
  const r = rng(hashSeed(seed + 'cluster'));
  const list = [];
  for (let i = 0; i < SHARDS; i++) {
    // The first point is the heart: centred, tallest, born at once. The rest
    // fill outward and are born progressively later, so growth reads as a
    // cluster spreading rather than one spike stretching.
    const heart = i === 0;
    const ring = heart ? 0 : 0.25 + Math.sqrt(r()) * 0.72;   // radial position on the rock
    const angle = heart ? 0 : r() * Math.PI * 2;
    const birth = heart ? 0 : Math.min(0.9, 0.03 + Math.pow(i / SHARDS, 1.2) * 0.88 + (r() - 0.5) * 0.05);
    const length = heart ? 1 : 0.3 + r() * 0.5 * (1 - ring * 0.35);
    const width = heart ? 0.2 : 0.08 + r() * 0.09;
    const lean = heart ? 0.05 : 0.12 + ring * 0.7 + (r() - 0.5) * 0.25;   // radians, outward
    const twist = r() * Math.PI * 2;
    const tip = 0.2 + r() * 0.25;                         // termination as a fraction of length
    list.push({ ring, angle, birth, length, width, lean, twist, tip });
  }
  return list;
}

/** How grown one shard is at growth g: 0 before its birth, 1 once mature. */
export function shardScale(shard, g) {
  const span = 0.18;
  const t = (g - shard.birth) / span;
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t * t * (3 - 2 * t);   // smoothstep
}
